import React, { useEffect, memo } from 'react';
import { useProject } from '@atlassian/jira-business-entity-project-hook';
import { useQueryBasedViewAutomaticTracking } from '@atlassian/jira-business-experience-tracking/src/controllers/experience-tracker/index.tsx';
import { useFilteredViewContextInput } from '@atlassian/jira-business-filters/src/controllers/default-query-input/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { ff } from '@atlassian/jira-feature-flagging';
import { ErrorFlag } from '@atlassian/jira-issue-error-flag';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { LOAD_GROUP_ITEMS_CHILDREN_EXPERIENCE } from '../../common/constants';
import type { GroupItemsQueryControllerProps } from '../../services/get-item-groups/types';
import { useListViewIssues as useListViewIssuesService } from '../../services/get-list-view-issues';
import { useIssueFields } from '../get-issue-fields';
import { useHierarchyConfig } from '../hierarchy/hierarchy-config';
import messages from './messages';

export const GroupItemsQueryController = memo<GroupItemsQueryControllerProps>(
	({ groupIssueId, groupQuery, onDataChange }: GroupItemsQueryControllerProps) => {
		const { hierarchyParent } = useHierarchyConfig();
		const { jqlContext, quickFiltersJql, sorting, isHidingDoneItems, jqlErrors } =
			useFilteredViewContextInput({
				view: 'list',
			});
		const isValidFiltersJql = !jqlErrors.length;
		let projectKey: string | undefined;
		if (ff('list-uses-new-jql-parent-project-directive_v5gfs')) {
			// eslint-disable-next-line react-hooks/rules-of-hooks
			const projectData = useProject();

			projectKey = projectData.key;
		}
		const {
			getQueryFieldIds,
			loading: isLoadingFields,
			isParentFieldVisible,
			isDevSummaryFieldVisible,
		} = useIssueFields();
		const {
			data,
			loading: isLoadingItems,
			error,
			loadNextPage,
			hasNextPage,
			isLoadingNextPage,
		} = useListViewIssuesService({
			input: {
				jqlContext,
				fieldIds: getQueryFieldIds(),
				quickFiltersJql,
				sorting,
				groupQuery,
				hierarchyParent,
				isHidingDoneItems,
				projectIdOrKey: projectKey,
				isParentFieldVisible: isParentFieldVisible(),
				isDevSummaryFieldVisible: isDevSummaryFieldVisible(),
			},
			condition: isLoadingFields === false && isValidFiltersJql,
		});
		const loading = !isValidFiltersJql ? false : isLoadingFields || isLoadingItems;
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const totalIssues = data?.length;

		useQueryBasedViewAutomaticTracking(LOAD_GROUP_ITEMS_CHILDREN_EXPERIENCE, {
			loading,
			error,
			shouldStartExperience: true,
		});

		useEffect(() => {
			if (loading || typeof totalIssues !== 'number') {
				return;
			}

			fireOperationalAnalytics(createAnalyticsEvent({}), 'listGroupItemsQuery succeeded', {
				totalIssues,
			});
		}, [loading, createAnalyticsEvent, totalIssues]);

		useEffect(() => {
			if (error) {
				fireErrorAnalytics({
					meta: {
						id: 'listGroupItemsQuery',
						packageName: 'jiraBusinessList',
						teamName: 'keystone',
					},
					attributes: {
						message: 'Failed to query group items in list view',
					},
					error,
					sendToPrivacyUnsafeSplunk: true,
				});
			}
		}, [error]);

		useEffect(() => {
			if (jqlErrors.length) {
				fireErrorAnalytics({
					meta: {
						id: 'listGroupItemsQuery',
						packageName: 'jiraBusinessList',
						teamName: 'keystone',
					},
					attributes: {
						message: 'Error in the JQL query',
					},
					error: jqlErrors[0],
					skipSentry: true,
				});
			}
		}, [jqlErrors]);

		useEffect(() => {
			onDataChange(groupIssueId, {
				data,
				loading,
				error,
				loadNextPage,
				isLoadingNextPage,
				hasNextPage,
			});
		}, [
			data,
			error,
			loading,
			loadNextPage,
			isLoadingNextPage,
			onDataChange,
			groupIssueId,
			hasNextPage,
		]);

		return error ? (
			<ErrorFlag error={error} title={messages.errorTitle} description={messages.errorMessage} />
		) : null;
	},
);
